import React, { useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ThemeContext } from '../../hooks/ThemeContext';
import { UserContext } from '../../hooks/UserContext';
import { api } from '../../services/baseUrl';

const ContactForm = ({ onSuccess, variant = 'default' }) => {
  const { theme } = useContext(ThemeContext);
  const { isAuthenticated, user } = useContext(UserContext);
  const isDark = theme === 'dark';
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.nickname || user?.firstname || '',
    email: user?.email || '',
    phone: '',
    service: '',
    requestType: '', // support, creation, autre
    message: ''
  });

  // Charger les catégories depuis l'API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories');
        setCategories(response.data?.data || response.data || []);
      } catch (error) {
        console.error('Erreur chargement catégories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.message.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }

    if (!isAuthenticated) {
      if (!formData.name.trim() || !formData.email.trim()) {
        toast.error('Le nom et l\'email sont requis');
        return;
      }
    }

    setLoading(true);

    try {
      const endpoint = isAuthenticated ? '/contact/send-message' : '/contact/send';
      const payload = isAuthenticated 
        ? {
            service: formData.service,
            request_type: formData.requestType,
            message: formData.message
          }
        : formData;

      const response = await api.post(endpoint, payload);
      
      if (response.data.success) {
        toast.success(response.data.message);
        
        // Réinitialiser le formulaire
        setFormData({
          name: user?.nickname || user?.firstname || '',
          email: user?.email || '',
          phone: '',
          service: '',
          requestType: '',
          message: ''
        });

        if (onSuccess) {
          onSuccess(response.data);
        }
      }
    } catch (error) {
      console.error('Erreur envoi message:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  // Variant minimal (pour accueil)
  if (variant === 'minimal') {
    return (
      <div className={`rounded-lg p-8 ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
        <h3 className="text-2xl font-bold mb-6">Envoyez-nous un message</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isAuthenticated && (
            <>
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="Votre nom"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                    isDark
                      ? 'bg-white/5 border-white/15 text-white placeholder-white/50 focus:border-rose-500 focus:bg-white/10'
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
                  } focus:outline-none`}
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Votre email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                    isDark
                      ? 'bg-white/5 border-white/15 text-white placeholder-white/50 focus:border-rose-500 focus:bg-white/10'
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
                  } focus:outline-none`}
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Votre téléphone (optionnel)"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                    isDark
                      ? 'bg-white/5 border-white/15 text-white placeholder-white/50 focus:border-rose-500 focus:bg-white/10'
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
                  } focus:outline-none`}
                />
              </div>
            </>
          )}

          {!isAuthenticated && (
            <div>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-white/5 border-white/15 text-white focus:border-rose-500 focus:bg-white/10'
                    : 'bg-white border-gray-200 text-gray-800 focus:border-rose-600'
                } focus:outline-none`}
              >
                <option value="">-- Sélectionnez un service (optionnel) --</option>
                {categories.map(cat => (
                  <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <textarea
              name="message"
              placeholder="Votre message..."
              value={formData.message}
              onChange={handleChange}
              required
              rows="4"
              className={`w-full px-4 py-2 rounded-lg border transition-colors resize-none ${
                isDark
                  ? 'bg-[#0b0b0c] border-white/10 text-white placeholder-white/60 focus:border-rose-600 caret-rose-400'
                  : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
              } focus:outline-none`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Envoi en cours...' : 'Envoyer le message'}
          </button>
        </form>
      </div>
    );
  }

  // Variant complet (pour page Contact)
  return (
    <div className="max-w-2xl mx-auto">
      {isAuthenticated && (
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-semibold text-sm">✓ Message direct à l'administration</p>
              <p className="text-xs mt-1">Votre message sera envoyé directement à l'administration et sera visible dans le panel admin. Vous recevrez une réponse par email.</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {!isAuthenticated && (
          <>
            <div>
              <label className="block text-sm font-medium mb-1">Nom</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-white/5 border-white/15 text-white placeholder-white/50 focus:border-rose-500 focus:bg-white/10'
                    : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
                } focus:outline-none`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-white/5 border-white/15 text-white placeholder-white/50 focus:border-rose-500 focus:bg-white/10'
                    : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
                } focus:outline-none`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Téléphone (optionnel)</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-2 rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-white/5 border-white/15 text-white placeholder-white/50 focus:border-rose-500 focus:bg-white/10'
                    : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
                } focus:outline-none`}
              />
            </div>
          </>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">Service {isAuthenticated ? '(optionnel)' : ''}</label>
          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            className={`w-full px-4 py-2 rounded-lg border transition-colors ${
              isDark
                ? 'bg-white/5 border-white/15 text-white focus:border-rose-500 focus:bg-white/10'
                : 'bg-white border-gray-200 text-gray-800 focus:border-rose-600'
            } focus:outline-none`}
          >
            <option value="">-- Sélectionnez --</option>
            {categories.map(cat => (
              <option key={cat.id || cat.name} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Type de demande</label>
          <select
            name="requestType"
            value={formData.requestType}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 rounded-lg border transition-colors ${
              isDark
                ? 'bg-white/5 border-white/15 text-white focus:border-rose-500 focus:bg-white/10'
                : 'bg-white border-gray-200 text-gray-800 focus:border-rose-600'
            } focus:outline-none`}
          >
            <option value="">-- Sélectionnez --</option>
            <option value="support">Support / Assistance</option>
            <option value="creation">Demande de création</option>
            <option value="autre">Autre demande</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Message</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows="6"
            className={`w-full px-4 py-2 rounded-lg border transition-colors resize-none ${
              isDark
                ? 'bg-[#0b0b0c] border-white/10 text-white placeholder-white/60 focus:border-rose-600 caret-rose-400'
                : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 focus:border-rose-600'
            } focus:outline-none`}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Envoi en cours...' : 'Envoyer le message'}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
